/**
 * Confirm-screen helpers for the server's `pickupInfo` (nearest auto, ETA, tip
 * chips). Pure functions so the wording rules can be unit-tested.
 *
 * `pickupInfo` shape (from getRideQuote, all server-computed):
 *   { nearestKm, etaMin, nearbyCount, onlineCount, level, tipAmounts, tipCap }
 *   level: 'near' | 'mid' | 'far' | 'veryFar' | 'none'
 * It is absent when the server is older or the lookup failed — display helpers
 * then show nothing extra, while refresh eligibility follows the quote's age.
 */

/** Seats in an auto, shown as the person icon next to the ETA. */
export const AUTO_SEATS = 3;

const LEVELS = new Set(['near', 'mid', 'far', 'veryFar', 'none']);

export const hasPickupInfo = (info) => Boolean(info && LEVELS.has(info.level));

/**
 * Retry an expired server quote even when availability was omitted. An older
 * server and a temporary fleet lookup failure have the same response shape;
 * neither should permanently disable recovery. The timestamp is the latest
 * quote response time, so missing availability still respects the refresh delay.
 */
export const pickupInfoIsStale = (routeInfo, now, maxAgeMs) => {
  if (!routeInfo?.isServerQuote) return false;
  const at = Number(routeInfo.pickupInfoAt);
  if (!Number.isFinite(at) || at <= 0) return false;
  return now - at > maxAgeMs;
};

/** The tip the rider picked, only if it is still one of the offered chips. */
export const selectedTip = (info, choice) => {
  const amount = Number(choice);
  if (!hasPickupInfo(info) || !Array.isArray(info.tipAmounts)) return 0;
  return info.tipAmounts.includes(amount) ? amount : 0;
};

/**
 * Which i18n keys (and variables) describe the pickup, or null for "say nothing".
 * `near` shows only the ETA line, no tip prompt.
 */
export const pickupMessage = (info) => {
  if (!hasPickupInfo(info) || info.level === 'near') return null;
  // Number(null) is 0 — a missing distance must stay blank, not read "0.0 km".
  const km = info.nearestKm == null ? NaN : Number(info.nearestKm);
  const vars = {
    km: Number.isFinite(km) ? km.toFixed(1) : '',
    min: info.etaMin ?? '',
  };
  if (info.level === 'none') {
    // No chips offered means nobody is online at all — a tip can't help, say so.
    const hasChips = Array.isArray(info.tipAmounts) && info.tipAmounts.length > 0;
    return { headline: 'book.pickupNone', hint: hasChips ? 'book.tipHintNone' : 'book.tipHintOffline', vars };
  }
  if (info.level === 'mid') return { headline: 'book.pickupMid', hint: 'book.tipHintMid', vars };
  return { headline: 'book.pickupFar', hint: 'book.tipHintFar', vars };
};

/**
 * "Drop 10:59 am": now + the auto's trip to the pickup + the ride itself.
 * Without a real pickup ETA (nobody coming, or an older server) there is no
 * honest drop time, so return '' — never treat a missing ETA as 0 minutes.
 */
export const dropTimeLabel = (info, rideDurationMin, now = new Date(), locale) => {
  const ride = Number(rideDurationMin);
  if (!Number.isFinite(ride) || ride <= 0) return '';
  if (!hasPickupInfo(info) || info.level === 'none' || info.etaMin == null) return '';
  const toPickup = Number(info.etaMin);
  if (!Number.isFinite(toPickup) || toPickup < 0) return '';
  const at = new Date(now.getTime() + (toPickup + ride) * 60000);
  try {
    return at.toLocaleTimeString(locale || undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};
